import { ISavedPageAddRequest } from "../models/SavedPage/ISavedPageAddRequest";

import { ISavedPageGetResponse } from "../models/SavedPage/ISavedPageGetResponse";

import { ISavedPageRequest } from "../models/SavedPage/ISavedPageRequest";

import { EventUtil } from "../util/EventUtil";
import {
  canonicalTargetType,
  deleteSavedItem,
  fetchSavedForSession,
  HealioSavedRow,
  isAliasedTargetType,
  mapRowToGetResponse,
  ParsedSavedMeta,
  targetTypeFromAddRequest,
  upsertSavedItem,
} from "./HealioWidgetsSavedApi";

export class SavedPageService {
  public static readonly SavedPageIncrementEvent: string =
    "SavedPageIncrementEvent";

  public static readonly SavedPageDecrementEvent: string =
    "SavedPageDecrementEvent";

  private readonly _savedPageRequests: Map<
    string,
    Promise<ReadonlyArray<ISavedPageGetResponse> | null>
  > = new Map();

  private _allRows: Promise<HealioSavedRow[]> | null = null;

  public save(
    savedPage: ISavedPageAddRequest,
    extras?: Partial<ParsedSavedMeta>
  ) {
    void upsertSavedItem(savedPage, extras).then(ok => {
      if (!ok) {
        console.error(
          "[SavedPageService] Unexpected response when saving page for user."
        );
        return;
      }
      this.clearCache();
      const event = EventUtil.createCustomEvent(
        SavedPageService.SavedPageIncrementEvent,
        savedPage.documentid,
        false
      );
      window.dispatchEvent(event);
    });
  }

  public async list(
    contentType: string
  ): Promise<Readonly<ISavedPageGetResponse[]> | null> {
    const cachedPromise = this._savedPageRequests.get(contentType);

    if (cachedPromise) {
      return cachedPromise;
    }

    const promise = this.load(contentType);
    this._savedPageRequests.set(contentType, promise);

    return promise;
  }

  public clearCache(contentType?: string): void {
    // The shared all-rows fetch backs every aliased type, so it goes with any clear.
    this._allRows = null;
    if (contentType) {
      this._savedPageRequests.delete(contentType);
    } else {
      this._savedPageRequests.clear();
    }
  }

  public remove(savedPage: ISavedPageRequest | ISavedPageAddRequest) {
    const addShape = savedPage as ISavedPageAddRequest;
    const targetType =
      addShape.contenttype && addShape.contenttype.length > 0
        ? targetTypeFromAddRequest(addShape)
        : "news";

    void this.deleteUnderAllNames(targetType, savedPage.documentid).then(ok => {
      if (!ok) {
        console.error(
          "[SavedPageService] Unexpected response when removing page for user."
        );
        return;
      }
      this.clearCache();
      const event = EventUtil.createCustomEvent(
        SavedPageService.SavedPageDecrementEvent,
        savedPage.documentid,
        false
      );
      window.dispatchEvent(event);
    });
  }

  /**
   * The API deletes by the exact stored type, so an item saved under its migrated
   * name (podcast-episode) is removed under that name as well as the live one.
   */
  private async deleteUnderAllNames(
    targetType: string,
    targetId: string
  ): Promise<boolean> {
    const types = new Set<string>();
    if (isAliasedTargetType(targetType)) {
      try {
        const wanted = canonicalTargetType(targetType);
        const id = targetId.toLowerCase();
        for (const row of await this.listAllRows()) {
          if (
            row.targetId.toLowerCase() === id &&
            canonicalTargetType(row.targetType || "") === wanted
          ) {
            types.add(row.targetType);
          }
        }
      } catch {
        // Fall back to the type the icon knows.
      }
    }
    // Only the types actually stored (a delete under an absent name may 404).
    if (types.size === 0) {
      types.add(targetType);
    }
    const results = await Promise.all(
      Array.from(types).map(type => deleteSavedItem({ targetType: type, targetId }))
    );
    return results.every(ok => ok);
  }

  /**
   * Every saved row for the session, fetched once and shared by all save icons on
   * the page (cleared on save / remove, or by clearCache).
   */
  public listAllRows(): Promise<HealioSavedRow[]> {
    if (!this._allRows) {
      this._allRows = fetchSavedForSession().catch(error => {
        this._allRows = null;
        throw error;
      });
    }
    return this._allRows;
  }

  private async load(
    contentType: string
  ): Promise<Readonly<ISavedPageGetResponse[]> | null> {
    try {
      let rows: HealioSavedRow[];
      if (isAliasedTargetType(contentType)) {
        const wanted = canonicalTargetType(contentType);
        rows = (await this.listAllRows()).filter(
          row => canonicalTargetType(row.targetType || "") === wanted
        );
      } else {
        rows = await fetchSavedForSession({ targetType: contentType });
      }
      if (rows.length < 1) {
        console.log("No saved pages found for user");
      }
      return rows.map(mapRowToGetResponse);
    } catch (error) {
      console.warn("[SavedPageService] load failed", error);
      return [];
    }
  }
}
